import get from 'lodash/get';
import isNumber from 'lodash/isNumber';
import omit from 'lodash/omit';
// import pick from 'lodash/pick';
import PropTypes from 'prop-types';
import {useState} from 'react';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Grid from "@mui/material/Grid";
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

// import * as validations from '../../helpers/validations';
import useUser from '../../hooks/useUser';
import {useCreatePayerPlanMutation, useGetPayerPlansQuery, useUpdatePayerPlanMutation} from '../../redux/services/payerPlanApi';
import FieldInput from '../common/FormInput';
import { useGetPatientsQuery } from '../../redux/services/patientApi';
import LoadingScreen from '../common/LoadingScreen';
import { SettingsInputComponentSharp } from '@mui/icons-material';

const sections = [
  {value: 'patient', label: '', billingTypes: ['COMMERCIAL', 'COURTESY_BILL', 'AUTO', 'WORK_COMP', 'LIEN', 'CORRECTIONS']}, // no heading
  {value: 'billing', label: 'Billing Details', billingTypes: ['COMMERCIAL', 'COURTESY_BILL', 'AUTO', 'WORK_COMP', 'LIEN', 'CORRECTIONS']},
  {value: 'subscriber', label: 'Subscriber Details', billingTypes: ['COMMERCIAL', 'CORRECTIONS']},
  {value: 'adjustor', label: 'Adjustor (Case Manager)', billingTypes: ['AUTO', 'WORK_COMP', 'LIEN']},
  {value: 'physician', label: 'Managing Physician', billingTypes: ['AUTO', 'WORK_COMP', 'LIEN']},
  {value: 'attorney', label: 'Attorney', billingTypes: ['AUTO', 'WORK_COMP', 'LIEN']},
];

const getFields = ({isPatient}) => ([
  {
    label: 'Patient',
    section: 'patient',
    name: 'patient_id',
    type: 'selectPatient',
    editableAfterCreate: false,
    required: true,
    fullWidth: true,
    hideFromPatient: true,
    allPatients: false,
    validate: () => true,
    grid: {
      xs: 12,
      md: 6,
    },
  },
  {
    label: 'Billing Type',
    section: 'billing',
    name: 'category',
    type: 'select',
    editableAfterCreate: false,
    helperText: null,
    placeholder: null,
    required: true,
    fullWidth: true,
    defaultValue: 'COMMERCIAL',
    validate: () => true,
    options: [
      {value: 'AUTO', label: 'Auto'},
      {value: 'WORK_COMP', label: 'Workers Comp'},
      {value: 'LIEN', label: 'Lien'},
      {value: 'COMMERCIAL', label: 'Commercial'},
      {value: 'CORRECTIONS', label: 'Corrections'},
    ].filter(
      o => isPatient ? o.value !== 'CORRECTIONS' : true
    ),
    grid: {
      xs: 12,
      md: 6
    },
  },
  {
    label: 'Assignment of Benefits',
    section: 'billing',
    name: 'should_assign_benefits',
    type: 'boolean',
    editableAfterCreate: true,
    helperText: null,
    placeholder: null,
    required: false,
    fullWidth: true,
    billingTypes: ['AUTO', 'WORK_COMP', 'LIEN', 'COMMERCIAL'],
    validate: () => true,
    grid: {
      xs: 12,
      md: 6
    }
  },
  {
    label: 'Insurance Carrier',
    section: 'billing',
    name: 'payer_id',
    type: 'selectPayer',
    editableAfterCreate: true,
    placeholder: 'Find your insurer or payer',
    helperText: null,
    required: true,
    fullWidth: true,
    validate: () => true,
    grid: {
      xs: 12,
      md: 6,
    }
  },
  {
    label: 'Primary insurance',
    section: 'billing',
    name: 'is_default',
    type: 'boolean',
    editableAfterCreate: true,
    helperText: null,
    placeholder: null,
    required: false,
    fullWidth: true,
    billingTypes: ['AUTO', 'WORK_COMP', 'LIEN', 'COMMERCIAL'],
    validate: () => true,
    grid: {
      xs: 12,
      md: 6
    },
  },
  {
    label: 'Estimated Copay, Co-Insurance, or Balance Bill',
    section: 'billing',
    name: 'copay',
    type: 'number',
    editableAfterCreate: true,
    helperText: null,
    placeholder: null,
    required: true,
    fullWidth: true,
    billingTypes: ['AUTO', 'WORK_COMP', 'LIEN', 'COMMERCIAL'],
    validate: () => true,
    grid: {
      xs: 12,
      md: 6
    }
  },
  {
    label: 'Coverage is currently active',
    section: 'billing',
    name: 'is_active',
    type: 'boolean',
    editableAfterCreate: true,
    helperText: null,
    placeholder: null,
    required: false,
    fullWidth: true,
    validate: () => true,
    grid: {
      xs: 12,
      md: 6
    },
  },
  {
    label: 'Date of Injury',
    section: 'billing',
    name: 'date_of_injury',
    type: 'simpleDate',
    editableAfterCreate: true,
    helperText: null,
    placeholder: null,
    required: true,
    billingTypes: ['AUTO', 'WORK_COMP', 'LIEN'],
    fullWidth: true,
    validate: () => true,
    grid: {
      xs: 12,
      md: 6
    }
  },
  {
    label: 'Claim Number',
    section: 'billing',
    name: 'claim_number',
    type: 'text',
    editableAfterCreate: true,
    placeholder: null,
    required: true,
    fullWidth: true,
    billingTypes: ['AUTO', 'WORK_COMP', 'LIEN'],
    validate: () => true,
    grid: {
      xs: 12,
      md: 6
    },
  },
  {
    label: 'Subscriber ID',
    section: 'subscriber',
    name: 'subscriber_id',
    type: 'text',
    editableAfterCreate: true,
    subscriberField: false,
    helperText: 'Often displayed as the ID on the insurance card.',
    placeholder: null,
    required: false,
    fullWidth: true,
    billingTypes: ['COMMERCIAL', 'CORRECTIONS'],
    validate: () => true,
    grid: {
      xs: 12,
      md: 6
    }
  },
  {
    label: 'The patient is the subscriber',
    section: 'subscriber',
    name: 'subscriber_is_patient',
    type: 'boolean',
    editableAfterCreate: true,
    subscriberField: false,
    required: false,
    fullWidth: true,
    validate: () => true,
    billingTypes: ['COMMERCIAL', 'CORRECTIONS'],
    grid: {
      xs: 12,
      md: 6
    },
  },
  {
    label: 'Subscriber First Name',
    section: 'subscriber',
    name: 'subscriber_first_name',
    type: 'text',
    editableAfterCreate: true,
    subscriberField: true,
    placeholder: null,
    required: true,
    fullWidth: true,
    billingTypes: ['COMMERCIAL', 'CORRECTIONS'],
    validate: () => true,
    grid: {
      xs: 12,
      md: 6
    }
  },
  {
    label: 'Subscriber Last Name',
    section: 'subscriber',
    name: 'subscriber_last_name',
    type: 'text',
    editableAfterCreate: true,
    subscriberField: true,
    placeholder: null,
    required: true,
    fullWidth: true,
    billingTypes: ['COMMERCIAL', 'CORRECTIONS'],
    validate: str => str && str.length > 0,
    grid: {
      xs: 12,
      md: 6
    }
  },
  {
    label: 'Subscriber Middle Initial',
    section: 'subscriber',
    name: 'subscriber_middle_initial',
    type: 'text',
    editableAfterCreate: true,
    subscriberField: true,
    placeholder: null,
    required: false,
    fullWidth: true,
    billingTypes: ['COMMERCIAL', 'CORRECTIONS'],
    validate: (str) => str && str.length === 1,
    grid: {
      xs: 12,
      md: 6
    }
  },
  {
    label: 'Subscriber Birth Date',
    section: 'subscriber',
    name: 'subscriber_date_birth',
    type: 'simpleDate',
    editableAfterCreate: true,
    subscriberField: true,
    placeholder: null,
    required: true,
    fullWidth: true,
    billingTypes: ['COMMERCIAL', 'CORRECTIONS'],
    validate: str => str && str.length > 0,
    grid: {
      xs: 12,
      md: 6
    }
  },
  {
    label: 'Subscriber Sex Assigned at Birth',
    section: 'subscriber',
    name: 'subscriber_sex',
    type: 'select',
    editableAfterCreate: true,
    subscriberField: true,
    placeholder: null,
    required: true,
    fullWidth: true,
    billingTypes: ['COMMERCIAL', 'CORRECTIONS'],
    validate: () => true,
    helperText: 'Payers require us to collect this information.',
    options: [
      {value: 'female', label: 'Female'},
      {value: 'male', label: 'Male'},
    ],
    grid: {
      xs: 12,
      md: 6
    },
  },
  {
    label: 'Relationship to Patient',
    section: 'subscriber',
    name: 'subscriber_relation',
    type: 'select',
    editableAfterCreate: true,
    subscriberField: true,
    placeholder: null,
    required: true,
    fullWidth: true,
    billingTypes: ['COMMERCIAL', 'CORRECTIONS'],
    validate: () => true,
    options: [
      {value: 'SELF', label: 'I am the patient'},
      {value: 'SPOUSE', label: 'The patient is my spouse'},
      {value: 'CHILD', label: 'The patient is my child'},
      {value: 'OTHER', label: 'Other'},
    ],
    grid: {
      xs: 12,
      md: 6
    },
  },
  {
    label: 'Subscriber Address',
    section: 'subscriber',
    name: 'subscriber_address',
    type: 'address',
    editableAfterCreate: true,
    subscriberField: true,
    placeholder: null,
    required: true,
    fullWidth: true,
    billingTypes: ['COMMERCIAL'],
    validate: () => true,
    grid: {
      xs: 12,
      md: 6
    },
  },
  // {
  //   label: 'Adjustor Name',
  //   section: 'adjustor',
  //   name: 'subscriber_address',
  //   type: 'address',
  //   editableAfterCreate: true,
  //   subscriberField: true,
  //   placeholder: null,
  //   required: true,
  //   fullWidth: true,
  //   billingTypes: ['AUTO', 'WORK_COMP', 'COMMERCIAL', 'LIEN'],
  //   validate: () => true,
  //   grid: {
  //     xs: 12,
  //     md: 6
  //   },
  // },
  {
    label: 'Name',
    section: 'adjustor',
    name: 'adjuster_name',
    type: 'text',
    editableAfterCreate: true,
    placeholder: null,
    required: false,
    fullWidth: true,
    billingTypes: ['AUTO', 'WORK_COMP', 'LIEN'],
    validate: () => true,
    grid: {
      xs: 12,
      md: 6
    },
  },
  {
    label: 'Phone Number',
    section: 'adjustor',
    name: 'adjuster_phone',
    type: 'phone',
    editableAfterCreate: true,
    placeholder: null,
    required: false,
    fullWidth: true,
    billingTypes: ['AUTO', 'WORK_COMP', 'LIEN'],
    validate: () => true,
    grid: {
      xs: 12,
      md: 6
    },
  },
  {
    label: 'Email',
    section: 'adjustor',
    name: 'adjuster_email',
    type: 'email',
    editableAfterCreate: true,
    placeholder: null,
    required: false,
    fullWidth: true,
    billingTypes: ['AUTO', 'WORK_COMP', 'LIEN'],
    validate: () => true,
    grid: {
      xs: 12,
      md: 6
    },
  },
  {
    label: 'Name',
    section: 'attorney',
    name: 'attorney_name',
    type: 'text',
    editableAfterCreate: true,
    placeholder: null,
    required: false,
    fullWidth: true,
    billingTypes: ['AUTO', 'WORK_COMP', 'LIEN'],
    validate: () => true,
    grid: {
      xs: 12,
      md: 6
    },
  },
  {
    label: 'Firm Name',
    section: 'attorney',
    name: 'attorney_firm_name',
    type: 'text',
    editableAfterCreate: true,
    placeholder: null,
    required: false,
    fullWidth: true,
    billingTypes: ['AUTO', 'WORK_COMP', 'LIEN'],
    validate: () => true,
    grid: {
      xs: 12,
      md: 6
    },
  },
  {
    label: 'Phone Number',
    section: 'attorney',
    name: 'attorney_phone',
    type: 'phone',
    editableAfterCreate: true,
    placeholder: null,
    required: false,
    fullWidth: true,
    billingTypes: ['AUTO', 'WORK_COMP', 'LIEN'],
    validate: () => true,
    grid: {
      xs: 12,
      md: 6
    },
  },
  {
    label: 'Email',
    section: 'attorney',
    name: 'attorney_email',
    type: 'email',
    editableAfterCreate: true,
    placeholder: null,
    required: false,
    fullWidth: true,
    billingTypes: ['AUTO', 'WORK_COMP', 'LIEN'],
    validate: () => true,
    grid: {
      xs: 12,
      md: 6
    },
  },
  {
    label: 'Name',
    section: 'physician',
    name: 'physician_name',
    type: 'text',
    editableAfterCreate: true,
    placeholder: null,
    required: false,
    fullWidth: true,
    billingTypes: ['AUTO', 'WORK_COMP', 'LIEN'],
    validate: () => true,
    grid: {
      xs: 12,
      md: 6
    },
  },
  {
    label: 'Practice Name',
    section: 'physician',
    name: 'physician_practice_name',
    type: 'text',
    editableAfterCreate: true,
    placeholder: null,
    required: false,
    fullWidth: true,
    billingTypes: ['AUTO', 'WORK_COMP', 'LIEN'],
    validate: () => true,
    grid: {
      xs: 12,
      md: 6
    },
  },
  {
    label: 'Phone Number',
    section: 'physician',
    name: 'physician_phone',
    type: 'phone',
    editableAfterCreate: true,
    placeholder: null,
    required: false,
    fullWidth: true,
    billingTypes: ['AUTO', 'WORK_COMP', 'LIEN'],
    validate: () => true,
    grid: {
      xs: 12,
      md: 6
    },
  },
]);

const getSubscriberFieldsFromPatient = patient => ({
  subscriber_first_name: patient.user.first_name,
  subscriber_last_name: patient.user.last_name,
  subscriber_middle_initial: patient.middle_initial || '',
  subscriber_date_birth: patient.date_birth,
  subscriber_sex: patient.sex,
  subscriber_phone: patient.patient_phone,
  subscriber_relation: 'SELF',
  subscriber_address: {
    address_line_1: patient.address_line_1,
    address_line_2: patient.address_line_2,
    address_city: patient.address_city,
    address_state: patient.address_state,
    address_zip: patient.address_zip,
  },
});

const parseFormValues = formValues => {
  let data = omit(formValues, 'copay');
  if (formValues?.copay) data.copay = parseFloat(formValues.copay);
  return data;
};

const handleFieldUpdate = ({formValues, setFormValues, patient}) => fieldName => (e, val) => {
  let value;
  let values = {...formValues};
  const boolFields = [
    'should_assign_benefits', 
    'is_default', 
    'is_active', 
    'subscriber_is_patient'
  ];
  if (fieldName === 'patient_id') {
    value = e?.patientId;
  } else if (fieldName === 'subscriber_address') {
    value = val;
  } else if (boolFields.includes(fieldName)) {
    value = e?.target?.checked;
  } else if (fieldName === 'payer_id') {
    value = val?.payerId;
  } else {
    value = e?.target?.value;
  }
  if (fieldName === 'subscriber_is_patient') {
    if (value) {
      values = {
        ...values,
        ...getSubscriberFieldsFromPatient(patient),
      };
    } else {
      values = {
        ...values,
        subscriber_first_name: null,
        subscriber_last_name: null,
        subscriber_middle_initial: null,
        subscriber_date_birth: null,
        subscriber_sex: null,
        subscriber_relation: null,
        subscriber_address: null,
      };
    }
  }
  values = {
    ...values,
    [fieldName]: value,
  };
  // console.log('newFormValues', values);
  return setFormValues(values);
};

const handleSubmit = ({
  editing, 
  id, 
  post, 
  put, 
  afterSubmit
}) => async (e, formValues) => {
  let request;
  if (editing) {
    request = await put({id, body: formValues});
  } else {
    request = await post(formValues);
  }
  if (!request?.error && afterSubmit) {
    afterSubmit(e, {id: id || request?.data?.id});
  }
};

const PayerPlanForm = props => {
  const {payerPlan: p, editing, afterSubmit, isPatient, patient} = props;
  const [formValues, setFormValues] = useState(
    props.editing 
    ? {...p} 
    : {
        category: 'COMMERCIAL', 
        patient_id: props.patientId, 
        subscriber_is_patient: true,
        is_active: true,
        ...getSubscriberFieldsFromPatient(patient),
      }
  );
  const [put, putResponse] = useUpdatePayerPlanMutation();
  const [post, postResponse] = useCreatePayerPlanMutation();
  const fields = getFields({isPatient})
    // if editing, only show editable fields
    .filter(f => isPatient ? !f.hideFromPatient : true)
    .filter(f => props.editing ? f.editableAfterCreate : true)
    .filter(f => formValues.subscriber_is_patient ? !f.subscriberField : true)
    // filter out billing fields that are not needed
    .filter(f => !f.billingTypes || f.billingTypes.includes(formValues.category));
  const onSubmit = handleSubmit({editing, id: p?.id, put, post, afterSubmit});
  const response = editing ? putResponse : postResponse;
  return (
    <Box sx={{width: '100%'}}>
      <Stack spacing={1} padding={1}>
        <Typography variant='h2' align='center'>
          {props.title || 'Payer Information'}
        </Typography>
        {
          <Grid container spacing={2} direction={{xs: 'row'}} padding={2}>
            {sections
              .filter(s => s.billingTypes.includes(formValues.category))
              .map(s => [
              s.label ?
                <Grid item xs={12} key={s.value}>
                  <Typography variant='h2' paddingTop={4} paddingBottom={1}>
                    {s.label}
                  </Typography>
                  <Divider/>
                </Grid> 
              : null,
              ...fields
                .filter(f => f.section === s.value)
                .map(f =>
                  <Grid 
                    key={f.name} 
                    item xs={f.grid?.xs ?? 12} 
                    sm={f.grid?.sm} 
                    md={f.grid?.md}
                    marginTop={f.type === 'boolean' ? 3.5 : 0}
                    padding={1}
                  >
                      <FieldInput
                        key={f.name + '-input'}
                        onChange={handleFieldUpdate({formValues, setFormValues, patient})(f.name)}
                        formValues={formValues}
                        {...f}
                        defaultValue={
                          editing ? p?.[f.name]
                          : (
                            f.defaultValue || 
                            (f.name === 'patient_id' ? props.patientId : null)
                            || (f.name === 'category' ? props.planCategory : null)
                            || (formValues?.[f.name] || null)
                          )
                        }
                      />
                  </Grid>
                )
            ])}
            {response.error && 
              <Grid item xs={12}>
                <Alert severity='error'>
                  {response.error?.data?.errors?.[0] || 'An error occurred while saving the payer plan.'}
                </Alert>
              </Grid>
            }
            <Grid item xs={12}>
              <Stack alignItems='center'>
                <Box sx={{width: 'fit-content'}}>
                  <Button
                    variant='primaryGradient'
                    disabled={props.disabled}
                    onClick={e => onSubmit(e, parseFormValues({...formValues, }))}
                  >
                    Save Changes
                  </Button>
                </Box>
              </Stack>
            </Grid>
          </Grid>
        }
      </Stack>
    </Box>
  );
};

// pre-load data before rendering the form
const PlanFormWrapper = props => {
  const {editing, payerPlanId, patientId} = props;
  const plansQuery = useGetPayerPlansQuery(
    {id: payerPlanId, patient_id: patientId},
    {skip: !editing || !payerPlanId || !patientId}
  );
  const payerPlan = plansQuery?.data?.data?.[0];
  const patientsQuery = useGetPatientsQuery({id: patientId}, {skip: !patientId});
  const patient = patientsQuery?.data?.data?.[0];
  const user = useUser();
  const isPatient = user && user?.is_patient;
  if (editing && !payerPlan || !patient) return <LoadingScreen/>;
  return (
    <PayerPlanForm
      {...props}
      patient={patient}
      isPatient={isPatient}
      payerPlan={payerPlan}
    />
  );
};

PlanFormWrapper.defaultProps = {
  onSubmitted: () => (true),
  editing: false,
};

PlanFormWrapper.propTypes = {
  patientId: PropTypes.number.isRequired,
  payerPlanId: PropTypes.number,
  onSubmitted: PropTypes.func,
  editing: PropTypes.bool,
  afterSubmit: PropTypes.func,
};

export default PlanFormWrapper;
